<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import svgIcon from '@jamescoyle/vue-icon'

import {
  Dialog,
  DialogTitle,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { MapPinIcon, ChevronRightIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { mdiTarget } from '@mdi/js'
import { useMainStore } from '~/store/MainStore'
import { useCartStore } from '~/store/CartStore'
import type { BpLocation } from '~/definitions/AllDefinitions'
import { getHoursFromAndTo } from '~/classes/HourUtils'

const _props = defineProps<{
  open: boolean
}>()

const emit = defineEmits(['close', 'locationSelected'])

const { t } = useI18n()
const mainStore = useMainStore()
const cartStore = useCartStore()
const { currentLocations, currentLocation } = storeToRefs(mainStore)
const { cartCount } = storeToRefs(cartStore)

const userLocation = ref<{ latitude: number, longitude: number } | null>(null)
const gettingLocation = ref(false)
const showConfirmation = ref(false)
const pendingLocation = ref<BpLocation | null>(null)

interface LocationWithDistance extends BpLocation {
  distance?: number
}

// Calculate distance between two coordinates using Haversine formula
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959 // Radius of Earth in miles
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const locationsWithDistance = computed(() => {
  if (!currentLocations.value) {
    return []
  }

  const locations: LocationWithDistance[] = currentLocations.value.map((location) => {
    const loc: LocationWithDistance = { ...location }

    if (userLocation.value && location.latitude && location.longitude) {
      loc.distance = calculateDistance(
        userLocation.value.latitude,
        userLocation.value.longitude,
        location.latitude,
        location.longitude,
      )
    }

    return loc
  })

  // Sort by distance if we have user location
  if (userLocation.value) {
    locations.sort((a, b) => (a.distance || 999) - (b.distance || 999))
  }

  return locations
})

function findNearestLocation() {
  gettingLocation.value = true

  if ('geolocation' in navigator) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        userLocation.value = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }
        gettingLocation.value = false
      },
      (error) => {
        console.error('Error getting location:', error)
        gettingLocation.value = false
      },
    )
  } else {
    console.error('Geolocation is not supported')
    gettingLocation.value = false
  }
}

function handleLocationClick(location: BpLocation) {
  // If clicking the current location, just close
  if (isCurrentLocation(location)) {
    emit('close')
    return
  }

  // If cart has items, show confirmation dialog
  if (cartCount.value > 0) {
    pendingLocation.value = location
    showConfirmation.value = true
  } else {
    // No items in cart, proceed directly
    confirmLocationChange(location)
  }
}

async function confirmLocationChange(location?: BpLocation) {
  const targetLocation = location || pendingLocation.value
  if (!targetLocation) {
    return
  }

  // Clear cart if switching locations
  if (cartCount.value > 0) {
    cartStore.clear()
  }

  await mainStore.getLocation(targetLocation.id)
  emit('locationSelected', targetLocation)

  // Reset state
  showConfirmation.value = false
  pendingLocation.value = null
  emit('close')
}

function cancelLocationChange() {
  showConfirmation.value = false
  pendingLocation.value = null
}

function formatDistance(distance?: number): string {
  if (!distance) {
    return ''
  }
  return `${distance.toFixed(1)} mi`
}

function getLocationHours(location: BpLocation): string {
  if (location.orderingHours) {
    return getHoursFromAndTo(location.orderingHours, t)
  } else if (location.businessHours) {
    return getHoursFromAndTo(location.businessHours, t)
  }
  return ''
}

function close() {
  emit('close')
}

function isCurrentLocation(location: BpLocation): boolean {
  return currentLocation.value?.id === location.id
}
</script>

<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="relative z-50"
      @close="close"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="tw-dialog-panel flex-col flex-1"
            >
              <div class="flex flex-col">
                <button
                  type="button"
                  class="tw-dialog-close-button"
                  @click="close"
                >
                  <span class="absolute -inset-0.5" />
                  <span class="sr-only">{{ t('CLOSE') }}</span>
                  <XMarkIcon
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                </button>
                <div class="mx-auto flex h-32 w-32 items-center justify-center mb-4">
                  <img src="/location-selector-hero@2x.png">
                </div>
                <!-- Title -->
                <DialogTitle
                  as="h2"
                  class="text-center text-lg text-slate-700 dark:text-slate-50 mb-6"
                >
                  {{ t('ORDER_DIALOG_HEADLINE') }}
                </DialogTitle>

                <div class="space-y-3 max-h-96 overflow-y-auto">
                  <!-- Find nearest location button -->
                  <button
                    v-if="!userLocation"
                    type="button"
                    :disabled="gettingLocation"
                    class="tw-button primary mb-6 w-auto mx-auto"
                    @click="findNearestLocation"
                  >
                    <svg-icon
                      type="mdi"
                      :path="mdiTarget"
                      class="mr-2 h-5 w-5"
                      aria-hidden="true"
                    />
                    <span>
                      {{ gettingLocation ? t('LOADING_X', { value: '...' }) : t('TAP_FIND_NEAREST_LOCATION') }}
                    </span>
                  </button>
                  <!-- Location list -->
                  <button
                    v-for="location in locationsWithDistance"
                    :key="location.id"
                    type="button"
                    class="w-full text-left rounded-lg bg-tw-2 border p-4 shadow-md dark:shadow-none"
                    :class="{
                      'border-blue-500 border-2 relative pt-6': isCurrentLocation(location),
                    }"
                    @click="handleLocationClick(location)"
                  >
                    <div class="absolute top-0 left-6 text-gray-500 inline-block">
                      <span
                        v-if="isCurrentLocation(location)"
                        class="inline-flex items-center rounded-full bg-tw-accent bg-opacity-10 py-1 text-xs font-medium text-tw-accent"
                      >
                        {{ t('CURRENT') }}
                      </span>
                    </div>
                    <div class="flex items-start justify-between">
                      <div class="flex items-start gap-3 flex-1">
                        <!-- Location icon -->
                        <div class="flex-shrink-0">
                          <div
                            :class="[
                              'w-10 h-10 rounded-lg flex items-center justify-center',
                              isCurrentLocation(location)
                                ? 'bg-tw-accent bg-opacity-20'
                                : 'bg-red-100',
                            ]"
                          >
                            <MapPinIcon
                              :class="[
                                'h-6 w-6',
                                isCurrentLocation(location) ? 'text-slate-700 dark:text-slate-50' : 'text-red-600',
                              ]"
                              aria-hidden="true"
                            />
                          </div>
                        </div>

                        <!-- Location details -->
                        <div class="flex-1 min-w-0 text-slate-700 dark:text-slate-50">
                          <div class="flex items-center gap-2 ">
                            <p class="font-semibold  text-sm">
                              {{ location.addressOne }}
                            </p>
                          </div>
                          <p class="font-semibold  text-sm">
                            {{ location.city }}, {{ location.state }} {{ location.postalCode }}
                          </p>
                          <div class="flex items-center gap-2 mt-1 text-sm text-gray-500">
                            <span v-if="location.distance">
                              {{ formatDistance(location.distance) }}
                            </span>
                            <span v-if="location.distance && getLocationHours(location)">|</span>
                            <span
                              v-if="getLocationHours(location)"
                              class="text-xs"
                            >
                              {{ t('OPEN') }}: {{ getLocationHours(location) }}
                            </span>
                          </div>
                        </div>

                        <!-- Arrow icon -->
                        <div class="flex-shrink-0 self-center">
                          <ChevronRightIcon
                            class="h-6 w-6 text-gray-400"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Confirmation Dialog -->
  <TransitionRoot
    as="template"
    :show="showConfirmation"
  >
    <Dialog
      as="div"
      class="relative z-[60]"
      @close="cancelLocationChange"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-[60] overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
              <div>
                <!-- Warning Icon -->
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                  <ExclamationTriangleIcon
                    class="h-6 w-6 text-yellow-600"
                    aria-hidden="true"
                  />
                </div>

                <!-- Content -->
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900"
                  >
                    {{ t('CHANGE_LOCATION') }}?
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      {{ t('CHANGE_LOCATION_WARNING') }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 sm:col-start-2"
                  @click="confirmLocationChange()"
                >
                  {{ t('CONTINUE') }}
                </button>
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                  @click="cancelLocationChange"
                >
                  {{ t('CANCEL') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
